/**
 * RealDataService - Main facade that imports and re-exports all modules
 * This maintains backward compatibility while organizing code into focused modules
 */

// Import all service modules
import { authService } from '@/shared/services/realData/auth';
import { clientService } from '@/shared/services/realData/clients';
import { quoteService } from '@/shared/services/realData/quotes';
import { jobService } from '@/shared/services/realData/jobs';
import { servicesService } from '@/shared/services/realData/services';
import { invoiceService } from '@/shared/services/realData/invoices';
import { expenseService } from '@/shared/services/realData/expenses';
import { calendarService } from '@/shared/services/realData/calendar';
import { fileService } from '@/shared/services/realData/files';
import { configService } from '@/shared/services/realData/config';
import { adminService } from '@/shared/services/realData/admin';
import { subscriptionService } from '@/shared/services/realData/subscription';
import { emailService } from '@/shared/services/realData/email';

/**
 * Main realDataService facade
 * Provides a unified interface to all service modules
 * Maintains backward compatibility with existing code
 */
export const realDataService = {
  // ==================== AUTHENTICATION METHODS ====================
  login: authService.login.bind(authService),
  loginWithGoogle: authService.loginWithGoogle.bind(authService),
  signup: authService.signup.bind(authService),
  logout: authService.logout.bind(authService),
  getProfile: authService.getProfile.bind(authService),
  fetchProfile: authService.fetchProfile.bind(authService),
  updateProfile: authService.updateProfile.bind(authService),
  getCurrentUserSupabaseId: authService.getCurrentUserSupabaseId.bind(authService),

  // ==================== CLIENT METHODS ====================
  getClients: clientService.getClients.bind(clientService),
  addClient: clientService.addClient.bind(clientService),
  updateClient: clientService.updateClient.bind(clientService),
  deleteClient: clientService.deleteClient.bind(clientService),
  getClientById: clientService.getClientById.bind(clientService),
  createClient: clientService.createClient.bind(clientService),

  // ==================== QUOTE METHODS ====================
  getQuotes: quoteService.getQuotes.bind(quoteService),
  addQuote: quoteService.addQuote.bind(quoteService),
  updateQuote: quoteService.updateQuote.bind(quoteService),
  deleteQuote: quoteService.deleteQuote.bind(quoteService),

  // ==================== JOB METHODS ====================
  getJobs: jobService.getJobs.bind(jobService),
  addJob: jobService.addJob.bind(jobService),
  mapQuoteStatusToJobStatus: jobService.mapQuoteStatusToJobStatus.bind(jobService),
  mapInvoiceStatusToJobStatus: jobService.mapInvoiceStatusToJobStatus.bind(jobService),

  // ==================== SERVICE METHODS ====================
  getServices: servicesService.getServices.bind(servicesService),
  addService: servicesService.addService.bind(servicesService),
  updateService: servicesService.updateService.bind(servicesService),
  deleteService: servicesService.deleteService.bind(servicesService),
  getServiceById: servicesService.getServiceById.bind(servicesService),
  createService: servicesService.createService.bind(servicesService),

  // ==================== INVOICE METHODS ====================
  getInvoices: invoiceService.getInvoices.bind(invoiceService),
  createInvoice: invoiceService.createInvoice.bind(invoiceService),
  recordPayment: invoiceService.recordPayment.bind(invoiceService),

  // ==================== EXPENSE METHODS ====================
  getExpenses: expenseService.getExpenses.bind(expenseService),
  createExpense: expenseService.createExpense.bind(expenseService),
  updateExpense: expenseService.updateExpense.bind(expenseService),
  deleteExpense: expenseService.deleteExpense.bind(expenseService),

  // ==================== CALENDAR METHODS ====================
  getEvents: calendarService.getEvents.bind(calendarService),
  createEvent: calendarService.createEvent.bind(calendarService),
  updateEvent: calendarService.updateEvent.bind(calendarService),
  deleteEvent: calendarService.deleteEvent.bind(calendarService),

  // ==================== FILE UPLOAD METHODS ====================
  uploadFile: fileService.uploadFile.bind(fileService),

  // ==================== EMAIL METHODS ====================
  sendEmail: emailService.sendEmail.bind(emailService),

  // ==================== SYSTEM CONFIG & HELPERS ====================
  getSystemConfig: configService.getSystemConfig.bind(configService),
  getServiceStatus: configService.getServiceStatus.bind(configService),
  formatCurrency: configService.formatCurrency.bind(configService),
  formatDate: configService.formatDate.bind(configService),
  generateId: configService.generateId.bind(configService),
  getPlatformProfile: configService.getPlatformProfile.bind(configService),
  getLogoUrl: configService.getLogoUrl.bind(configService),
  getPlanLimits: configService.getPlanLimits.bind(configService),
  getTemplates: configService.getTemplates.bind(configService),
  get isUsingRealServices(): boolean {
    return configService.isUsingRealServices;
  },

  // ==================== ADMIN METHODS ====================
  adminUpdateSystemConfig: adminService.adminUpdateSystemConfig.bind(adminService),
  adminResetSystem: adminService.adminResetSystem.bind(adminService),
  adminGetStats: adminService.adminGetStats.bind(adminService),
  adminGetAllUsers: adminService.adminGetAllUsers.bind(adminService),
  adminDeleteUser: adminService.adminDeleteUser.bind(adminService),
  adminResetUserPassword: adminService.adminResetUserPassword.bind(adminService),
  adminUpdateUserPlan: adminService.adminUpdateUserPlan.bind(adminService),
  adminSuspendUser: adminService.adminSuspendUser.bind(adminService),
  adminReactivateUser: adminService.adminReactivateUser.bind(adminService),
  adminGetUserActivity: adminService.adminGetUserActivity.bind(adminService),
  adminExportUserData: adminService.adminExportUserData.bind(adminService),
  adminGetTransactions: adminService.adminGetTransactions.bind(adminService),
  adminGetAuditLogs: adminService.adminGetAuditLogs.bind(adminService),
  adminExportAuditLogs: adminService.adminExportAuditLogs.bind(adminService),
  adminCreateAuditLog: adminService.adminCreateAuditLog.bind(adminService),
  getPublicEntity: adminService.getPublicEntity.bind(adminService),

  // ==================== SUBSCRIPTION METHODS ====================
  calculatePlanPrice: subscriptionService.calculatePlanPrice.bind(subscriptionService),
  recordSubscriptionPayment: subscriptionService.recordSubscriptionPayment.bind(subscriptionService),
  getSubscriptionDaysRemaining: subscriptionService.getSubscriptionDaysRemaining.bind(subscriptionService),
  getTransactions: subscriptionService.getTransactions.bind(subscriptionService)
};

// Re-export individual services for direct access if needed
export { authService } from '@/shared/services/realData/auth';
export { clientService } from '@/shared/services/realData/clients';
export { quoteService } from '@/shared/services/realData/quotes';
export { jobService } from '@/shared/services/realData/jobs';
export { servicesService } from '@/shared/services/realData/services';
export { invoiceService } from '@/shared/services/realData/invoices';
export { expenseService } from '@/shared/services/realData/expenses';
export { calendarService } from '@/shared/services/realData/calendar';
export { fileService } from '@/shared/services/realData/files';
export { configService } from '@/shared/services/realData/config';
export { adminService } from '@/shared/services/realData/admin';
export { subscriptionService } from '@/shared/services/realData/subscription';
export { emailService } from '@/shared/services/realData/email';
